import { useCallback, useEffect, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getDashboardWidgets } from "../../services";
import { DashboardWidget } from "../../types/dashboard.type";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { useNavigate } from "react-router-dom";
import Widget from "./Widget";
import RouteTable from "./RouteTable";
import SaleSummary from "./SaleSummary";
import ProfitAndCost from "./ProfitAndCost";
import ReportTable from "../Report/ReportList/Table";
import WidgetSkeleton from "./Skeleton/WidgetsSkeleton";
import SalesTargetSummary from "./SalesTargetSummary";
dayjs.locale("th");

export default function Dashboard() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [widgets, setWidgets] = useState<DashboardWidget>();
  const [triggerFetch, setTriggerFetch] = useState<number>(0);

  const onGetWidgets = useCallback(async (date: Dayjs) => {
    const {
      data: { data },
    } = await getDashboardWidgets(date.format("YYYY-MM-DD"));

    setWidgets(data);
  }, []);

  useEffect(() => {
    if (selectedDate) {
      onGetWidgets(selectedDate);
    }
  }, [triggerFetch, selectedDate, onGetWidgets]);

  const handlePendingCancelClick = () => {
    navigate("/sales?status=pending_cancel");
  };

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">ภาพรวมระบบ</Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              วว/ดด/ปปปป
            </Typography>
            <DatePicker
              value={selectedDate}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
              sx={{ bgcolor: "#fff" }}
              onChange={setSelectedDate}
            />
          </Box>
        </LocalizationProvider>
      </Box>
      <Grid container spacing={2}>
        {!widgets && <WidgetSkeleton length={5} size={2.4} />}
        {widgets && (
          <>
            <Grid size={2.4}>
              <Widget
                title="จำนวนบิลที่ขายได้วันนี้"
                total={widgets?.total_count ?? 0}
                icon={<LocalAtmIcon />}
                type="white"
              />
            </Grid>
            <Grid size={2.4}>
              <Widget
                title="รายงานยอดขายรอตรวจสอบ"
                total={widgets?.sale_report_branch_account_approved_count ?? 0}
                icon={<HistoryOutlinedIcon />}
                type="primary"
              />
            </Grid>
            <Grid size={2.4}>
              <Widget
                title="รายงานยอดขายที่รอแก้ไข"
                total={widgets?.sale_report_ho_request_change_count ?? 0}
                icon={<RateReviewOutlinedIcon />}
                type="link"
              />
            </Grid>
            <Grid size={2.4}>
              <Widget
                title="จำนวนของเสียวันนี้"
                total={widgets?.reconcile_total_waste_count ?? 0}
                icon={<DeleteOutlineOutlinedIcon />}
                type="warning"
              />
            </Grid>
            <Grid size={2.4}>
              <Widget
                title="คำขอยกเลิก Transactions"
                total={widgets?.pending_cancel_count ?? 0}
                icon={<ReportProblemOutlinedIcon />}
                type="error"
                onClick={handlePendingCancelClick}
              />
            </Grid>
          </>
        )}
        <Grid size={12}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <SaleSummary selectedDate={selectedDate} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <SalesTargetSummary selectedDate={selectedDate} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <ProfitAndCost selectedDate={selectedDate} />
            </Grid>
          </Grid>
        </Grid>
        <Grid size={12}>
          <RouteTable
            selectedDate={selectedDate}
            triggerFetch={triggerFetch}
            setTriggerFetch={setTriggerFetch}
          />
        </Grid>
        <Grid size={12}>
          <ReportTable
            showTitle={true}
            showFilter={false}
            showPagination={false}
            fixedLimit={7}
            defaultDate={selectedDate}
            triggerFetch={triggerFetch}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
